import { Link, useNavigate, useSearchParams } from 'react-router';
import { Columns3, LayoutList } from 'lucide-react';
import { Button, Card, cn, EmptyState } from '@vwt/ui';
import { POLL_ACTIVE, useApiQuery } from '../../lib/api.js';
import { useTranslation } from '../../lib/i18n.js';
import { KeyLabel, PhaseBadge, PriorityLabel } from '../../components/badges.js';
import { QueryState, UserChip } from '../../components/common.js';
import { WorkBoard } from './work-board.js';

/** Current list/board choice of a project or team page (kept in the URL so it can be shared). */
export function useScopedView(): 'list' | 'board' {
  const [params] = useSearchParams();
  return params.get('view') === 'board' ? 'board' : 'list';
}

/**
 * Work of one project or team as a list or as the phase board. Opening an item goes to the work
 * page filtered to the same scope, where the detail drawer and every filter are available.
 */
export function ScopedWork({ scope }: { scope: { projectId: string } | { teamId: string } }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const view = useScopedView();
  const work = useApiQuery('listWorkItems', { query: { ...scope, limit: view === 'board' ? '100' : '50' } }, { refetchInterval: POLL_ACTIVE });
  const scopeQuery = 'projectId' in scope ? `projectId=${scope.projectId}` : `teamId=${scope.teamId}`;
  const open = (id: string) => void navigate(`/work?selected=${id}&${scopeQuery}${view === 'board' ? '&view=board' : ''}`);
  const setView = (next: 'list' | 'board') =>
    setParams(
      (p) => {
        const nextParams = new URLSearchParams(p);
        if (next === 'board') nextParams.set('view', 'board');
        else nextParams.delete('view');
        return nextParams;
      },
      { replace: true },
    );
  const items = work.data?.data ?? [];
  return (
    <Card className={cn(view === 'board' && 'overflow-hidden')}>
      <div className="flex items-center justify-between border-b border-border px-3 py-2">
        <span className="text-xs font-medium text-fg-subtle">{t('work.title')}</span>
        <div className="inline-flex rounded-[var(--radius)] border border-border-strong p-0.5" role="group" aria-label="View">
          <Button size="sm" variant={view === 'list' ? 'secondary' : 'ghost'} onClick={() => setView('list')} aria-pressed={view === 'list'}>
            <LayoutList /> {t('work.views.list')}
          </Button>
          <Button size="sm" variant={view === 'board' ? 'secondary' : 'ghost'} onClick={() => setView('board')} aria-pressed={view === 'board'}>
            <Columns3 /> {t('work.views.board')}
          </Button>
        </div>
      </div>
      <QueryState query={work} empty={items.length === 0 ? <EmptyState title={t('work.empty')} /> : undefined}>
        {view === 'board' ? (
          <WorkBoard items={items} onOpen={open} />
        ) : (
          <ul className="divide-y divide-border">
            {items.map((w) => (
              <li key={w.id}>
                <Link to={`/work?selected=${w.id}&${scopeQuery}`} className="flex items-center gap-3 px-4 py-2.5 hover:bg-surface-2">
                  <PriorityLabel priority={w.priority} compact />
                  <KeyLabel value={w.key} />
                  <span className="min-w-0 flex-1 truncate text-sm font-medium">{w.title}</span>
                  <UserChip user={w.assignee} />
                  <PhaseBadge phase={w.phase} />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </QueryState>
      {work.data?.meta.nextCursor ? (
        <Link to={`/work?${scopeQuery}${params.get('view') === 'board' ? '&view=board' : ''}`} className="block border-t border-border px-4 py-2 text-xs text-fg-muted hover:bg-surface-2">
          {t('common.openFullPage')}
        </Link>
      ) : null}
    </Card>
  );
}
